/**
 * Document Template Page
 *
 * 文章テンプレート - マスタメンテナンス
 * Display and manage document templates.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useMainDocumentTemplateInit,
  useMainDocumentTemplates,
  useMainDocumentTemplateCreate,
  useMainDocumentTemplateUpdate,
  useMainDocumentTemplateDelete,
  useMainDocumentTemplateBatchUpdate,
} from '@/hooks/main/useMainDocumentTemplate';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { type AddressResult } from '@/hooks/useAddressSearch';
import { AddressSearchModal } from '@/components/organisms';
import { AppSpin } from '@/components/atoms';
import { useTheme } from '@/shared/providers/theme/ThemeContext';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import {
  DocumentTemplatePractitionerSection,
  DocumentTemplateListSection,
  DocumentTemplateEditModal,
} from '@/components/organisms/documentTemplate';
import type {
  DocumentTemplate,
  DocumentTemplateFormData,
  PractitionerFormData,
  DocumentTemplateSearchParams,
} from '@/types/documentTemplate.types';
import type { DocumentTemplateFormData as DocumentTemplateFormDataSchema } from '@/schemas/documentTemplateSchema';
import { getApiErrorMessage } from '@/utils/apiError';
import { DEFAULT_PER_PAGE } from '@/constants';

const EMPTY_PRACTITIONER_FORM: PractitionerFormData = {
  practitioner_name: '',
  representative_name: '',
  practitioner_postal_code: '',
  practitioner_address: '',
  practitioner_telephone: '',
};

/**
 * Main Document Template Page Component
 */
export interface DocumentTemplatePageProps {
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
}

export const DocumentTemplatePage: React.FC<DocumentTemplatePageProps> = ({
  canCreate = true,
  canUpdate = true,
  canDelete = true,
}) => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();

  // State
  const [currentPage, setCurrentPage] = useState(1);

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const { confirm } = useAppModal();

  // UI state
  const toast = useAppToast();

  // Practitioner form state (for batch update)
  const [practitionerForm, setPractitionerForm] =
    useState<PractitionerFormData>(EMPTY_PRACTITIONER_FORM);
  const [addressLookupOpen, setAddressLookupOpen] = useState(false);
  const [practitionerErrors, setPractitionerErrors] = useState<
    Partial<Record<keyof PractitionerFormData, string>>
  >({});
  /** Only auto-fill practitioner form from first template once on initial load; avoid re-filling after user clears. */
  const hasInitialPractitionerFormFilled = useRef(false);

  // Build search params
  const searchParams: DocumentTemplateSearchParams = useMemo(
    () => ({
      per_page: DEFAULT_PER_PAGE,
      page: currentPage,
    }),
    [currentPage]
  );

  // React Query hooks
  const { data: initResponse } = useMainDocumentTemplateInit();
  const {
    data: listResponse,
    isFetching,
    isPlaceholderData,
    error: listError,
    refetch: refetchTemplateList,
  } = useMainDocumentTemplates(searchParams);

  // Use isFetching for table loading (shows spinner during refetch/pagination)
  const loading = isFetching;

  // Mutations
  const createMutation = useMainDocumentTemplateCreate();
  const updateMutation = useMainDocumentTemplateUpdate();
  const deleteMutation = useMainDocumentTemplateDelete();
  const batchUpdateMutation = useMainDocumentTemplateBatchUpdate();

  // Extract data from responses
  const documentTypes = initResponse?.data?.document_types || [];
  const suffixTypes = initResponse?.data?.suffix_types || [];
  const templates = listResponse?.data?.data || [];
  const pagination = listResponse?.data?.pagination || null;

  useEffect(() => {
    hasInitialPractitionerFormFilled.current = false;
    setCurrentPage(1);
    setPractitionerForm(EMPTY_PRACTITIONER_FORM);
    setPractitionerErrors({});
  }, [tenant, clinicNumber, serviceType]);

  // Set initial practitioner form data from first template (once only; do not re-fill after user clears)
  useEffect(() => {
    if (!listResponse || isPlaceholderData || hasInitialPractitionerFormFilled.current) return;

    hasInitialPractitionerFormFilled.current = true;
    const firstTemplate = listResponse.data.data[0];
    const initialData = firstTemplate
      ? {
          practitioner_name: firstTemplate.practitioner_name || '',
          representative_name: firstTemplate.representative_name || '',
          practitioner_postal_code: firstTemplate.practitioner_postal_code || '',
          practitioner_address: firstTemplate.practitioner_address || '',
          practitioner_telephone: firstTemplate.practitioner_telephone || '',
        }
      : EMPTY_PRACTITIONER_FORM;

    setPractitionerForm(initialData);
  }, [isPlaceholderData, listResponse]);

  // Handle errors
  useEffect(() => {
    if (listError) {
      toast.error('データの取得に失敗しました。');
    }
  }, [listError, toast]);

  // detail API is handled inside the edit modal (page only passes templateId)

  // Handle page change
  const handlePageChange = (page: number) => {
    if (!loading) {
      setCurrentPage(page);
    }
  };

  // Handle new template
  const handleNewClick = () => {
    if (!canCreate) return;
    setEditingTemplateId(null);
    setIsNewTemplate(true);
    setEditModalOpen(true);
  };

  // Handle edit click - fetch full details via API
  const handleEditClick = (template: DocumentTemplate) => {
    if (!canUpdate) return;
    setEditingTemplateId(template.id);
    setIsNewTemplate(false);
    setEditModalOpen(true);
  };

  const handleSave = async (data: DocumentTemplateFormDataSchema) => {
    if ((isNewTemplate && !canCreate) || (!isNewTemplate && !canUpdate)) return;
    if (data.document_type_id == null || data.suffix_type_id == null) return;
    const payload: DocumentTemplateFormData = {
      ...data,
      document_type_id: data.document_type_id,
      suffix_type_id: data.suffix_type_id,
      content_prefix: data.content_prefix ?? '',
      content: data.content ?? '',
      content_suffix: data.content_suffix ?? '',
    };
    try {
      if (isNewTemplate) {
        await createMutation.mutateAsync(payload);
        toast.success('文章テンプレートを登録しました。');
      } else if (editingTemplateId) {
        await updateMutation.mutateAsync({ id: editingTemplateId, data: payload });
        toast.success('文章テンプレートを編集しました。');
      }
      setEditModalOpen(false);
      setEditingTemplateId(null);
      setIsNewTemplate(false);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, '保存に失敗しました。'));
      throw err;
    }
  };

  // Handle delete click: pass template into onOk so delete runs with correct item (avoid stale state)
  const handleDeleteClick = (template: DocumentTemplate) => {
    if (!canDelete) return;
    confirm({
      title: '削除確認',
      content: (
        <div>
          <p>この文章テンプレートを削除してもよろしいですか？</p>
        </div>
      ),
      okText: '削除',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(template.id);
          toast.success('削除しました。');
        } catch (err: unknown) {
          toast.error(getApiErrorMessage(err, '削除に失敗しました。'));
        } finally {
          await refetchTemplateList();
        }
      },
    });
  };

  // Handle practitioner form input change
  const handlePractitionerInputChange = (field: keyof PractitionerFormData, value: string) => {
    setPractitionerForm(prev => ({ ...prev, [field]: value }));
    if (practitionerErrors[field]) {
      setPractitionerErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Handle address lookup result (from modal or auto-fill)
  const handleAddressSelect = (result: AddressResult) => {
    const postalCode = result.postal_code1 + result.postal_code2;
    setPractitionerForm(prev => ({
      ...prev,
      practitioner_postal_code: postalCode || prev.practitioner_postal_code,
      practitioner_address: result.address || prev.practitioner_address,
    }));
    setAddressLookupOpen(false);
  };

  // Handle batch update
  const handleBatchUpdate = async () => {
    if (!canUpdate) return;
    if (templates.length === 0) {
      toast.warning('更新対象のテンプレートがありません。');
      return;
    }

    try {
      setPractitionerErrors({});

      // Create batch update items for all templates
      const batchItems = templates.map(template => ({
        id: template.id,
        practitioner_name: practitionerForm.practitioner_name,
        representative_name: practitionerForm.representative_name,
        practitioner_postal_code: practitionerForm.practitioner_postal_code,
        practitioner_address: practitionerForm.practitioner_address,
        practitioner_telephone: practitionerForm.practitioner_telephone,
      }));

      const response = await batchUpdateMutation.mutateAsync(batchItems);
      toast.success(response.message || '施術所情報を更新しました。');

      setPractitionerErrors({});
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { data?: { errors?: Record<string, string[]> } };
      };
      const apiErrors = axiosError.response?.data?.errors;
      const formFields: (keyof PractitionerFormData)[] = [
        'practitioner_name',
        'representative_name',
        'practitioner_postal_code',
        'practitioner_address',
        'practitioner_telephone',
      ];
      if (apiErrors && typeof apiErrors === 'object') {
        const fieldErrors: Partial<Record<keyof PractitionerFormData, string>> = {};
        Object.entries(apiErrors).forEach(([key, value]) => {
          const msg = Array.isArray(value) ? value[0] : String(value);
          const field = (
            key.includes('.') ? key.split('.').pop() : key
          ) as keyof PractitionerFormData;
          if (formFields.includes(field)) fieldErrors[field] = msg;
        });
        setPractitionerErrors(fieldErrors);
        return;
      }
      toast.error('更新に失敗しました。');
    }
  };

  return (
    <AppSpin spinning={loading}>
      <div className="pb-24 space-y-4">
        {/* Part 1: Practitioner Section */}
        <DocumentTemplatePractitionerSection
          value={practitionerForm}
          onChange={handlePractitionerInputChange}
          onSubmit={handleBatchUpdate}
          errors={practitionerErrors}
          submitting={batchUpdateMutation.isPending}
          disabled={!canUpdate}
        />

        {/* Part 2: List Section */}
        <DocumentTemplateListSection
          templates={templates}
          pagination={pagination}
          onNew={handleNewClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onPageChange={handlePageChange}
          disableCreate={!canCreate}
          disableEdit={!canUpdate}
          disableDelete={!canDelete}
        />

        <DocumentTemplateEditModal
          templateId={editingTemplateId}
          documentTypes={documentTypes}
          suffixTypes={suffixTypes}
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditingTemplateId(null);
            setIsNewTemplate(false);
            queryClient.invalidateQueries({
              predicate: query => query.queryKey[0] === 'document-templates' && !query.queryKey.includes('detail'),
            });
          }}
          onSave={handleSave}
          isNew={isNewTemplate}
        />

        {/* Address Search Modal */}
        <AddressSearchModal
          isOpen={addressLookupOpen}
          onClose={() => setAddressLookupOpen(false)}
          onSelect={handleAddressSelect}
          initialPostalCode1={practitionerForm.practitioner_postal_code.slice(0, 3)}
          initialPostalCode2={practitionerForm.practitioner_postal_code.slice(3, 7)}
          initialAddress={
            !practitionerForm.practitioner_postal_code
              ? (practitionerForm.practitioner_address ?? '')
              : ''
          }
          addressType="patient"
          title="住所補助"
        />
      </div>
    </AppSpin>
  );
};

export default DocumentTemplatePage;
